import { destroyProducts, useExportProductsXlsx, useProducts } from '@gozoki/api';
import { Button, Chip, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { PRODUCTS_ROUTES, ROUTE_BUILDER } from '../../utils/constants/routes.constants';

import { includesLowerCase } from '@gozoki/tools';
import { GridColDef } from '@mui/x-data-grid';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/communication/Toast';
import SimpleDataGrid from '../../components/containers/SimpleDataGrid';
import ImportExportIcon from '../../components/icons/ImportExportIcon';
import PlusIcon from '../../components/icons/PlusIcon';
import Page from '../../components/Page';
import { downloadBlob } from '../../utils/excel/downloadBlob';
import { useRights } from '../../utils/hooks';
import { fonts } from '../../utils/theme/fonts.theme';
import { ButtonOverrides } from '../../utils/theme/overrides.theme';

const TAB_WIDTH = 170;
const SMALL_TAB = 100;
const MEDIUM_WIDTH = 200;
const BIGGER_TAB = 230;

const productColDefs: GridColDef[] = [
    {
        field: 'reference',
        headerName: 'EAN 13',
        width: TAB_WIDTH,
    },
    {
        field: 'dun14',
        headerName: 'DUN 14',
        width: TAB_WIDTH,
    },
    { field: 'label', headerName: 'Libellé', width: BIGGER_TAB },
    {
        field: 'internalCategory',
        headerName: 'Catégorie interne',
        width: MEDIUM_WIDTH,
        renderCell: (field) => <Chip label={field.value} />,
    },
    {
        field: 'categories',
        headerName: 'Catégories Commerciales',
        width: BIGGER_TAB,
        renderCell: (field) =>
            field.value?.map((cat: string) => (
                <Chip key={`category-${cat}`} label={cat} sx={{ mx: '2px' }} />
            )),
    },
    { field: 'brand', headerName: 'Marque', width: TAB_WIDTH },
    {
        field: 'isNew',
        headerName: 'Nouveauté',
        width: SMALL_TAB,
        renderCell: (field) => (field.value === 'oui' ? <Chip label={field.value} /> : null),
    },
    {
        field: 'isActive',
        headerName: 'Activé',
        width: SMALL_TAB,
        renderCell: (field) => (field.value === 'oui' ? <Chip label={field.value} /> : null),
    },
    {
        field: 'antiGaspi',
        headerName: 'Anti-Gaspi',
        width: SMALL_TAB,
    },
    {
        field: 'copilot',
        headerName: 'Copilot',
        width: TAB_WIDTH,
    },
];

const ProductsPage = () => {
    const navigate = useNavigate();
    const products = useProducts();
    const [Toast, showToast] = useToast();

    const isUserAdmin = useRights(['ADMIN']);

    const { mutate: exportProductsXlsx, isPending: isExportingProductsXlsx } =
        useExportProductsXlsx();

    // ****************************************************** //
    //                    PRODUCT VARIABLES                   //
    // ****************************************************** //

    const [productSearch, setProductSearch] = useState('');
    const [productsSelection, setproductsSelection] = useState<number[]>([]);

    // ****************************************************** //
    //           QUERY TO DATAGRID TRANSFORMATION             //
    // ****************************************************** //

    // Transform fetched products into DataGrid format
    const productRows = useMemo(() => {
        if (products.data === undefined) {
            return [];
        }
        return products.data
            .filter((product) => product.isActive)
            .filter((product) => includesLowerCase(product.label, productSearch))
            .map((product) => ({
                id: product.id, // Datagrid requires a unique id field
                reference: product.reference,
                dun14: product.dun14,
                label: product.label,
                internalCategory: product.internalCategory.name,
                categories: product.categories.map((c) => c.name),
                brand: product.brand.name,
                isNew: product.isNew ? 'oui' : 'non',
                isActive: product.isActive ? 'oui' : 'non',
                antiGaspi: `J-${product.antiGaspi}`,
                copilot: product.copilotCode,
            }));
    }, [products.data, productSearch]);

    // ****************************************************** //
    //                         MUTATIONS                      //
    // ****************************************************** //

    const deleteProductMut = useMutation({
        mutationFn: destroyProducts,
    });

    // ****************************************************** //
    //                         HANDLERS                       //
    // ****************************************************** //

    const navigateAndUpdateProduct = async () => {
        // Navigate to first step with current selected id
        navigate(ROUTE_BUILDER.updateProductFirstStep(productsSelection[0]));
    };

    const navigateToProductDetails = async () => {
        // Navigate to first step with current selected id
        navigate(ROUTE_BUILDER.productDetails(productsSelection[0]));
    };

    const handleExportProductsXlsx = () => {
        exportProductsXlsx(undefined, {
            onSuccess: (data) => {
                downloadBlob({ blob: data, filename: 'products.xlsx' });
            },
            onError: (error) => {
                showToast({
                    severity: 'error',
                    message: error.message,
                });
            },
        });
    };

    const productsDisabled = useMemo(() => {
        return productsSelection.length === 0 || deleteProductMut.isPending;
    }, [productsSelection, deleteProductMut.isPending]);

    return (
        <Page>
            <Toast />
            <div style={{ display: 'flex' }}>
                <Typography style={fonts.pageTitle} flexGrow={1}>
                    Produits
                </Typography>

                <Button
                    variant="outlined"
                    color="secondary"
                    sx={{
                        marginRight: '16px',
                        backgroundColor: 'white',
                        ...ButtonOverrides.shadow,
                    }}
                    onClick={() => {
                        navigate(PRODUCTS_ROUTES.ADD_PRODUCT_FIRST_STEP);
                    }}
                    disabled={!isUserAdmin}
                >
                    <PlusIcon orange />
                    Ajouter un produit
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    sx={{
                        marginRight: '16px',
                    }}
                    onClick={handleExportProductsXlsx}
                    disabled={!isUserAdmin}
                >
                    <ImportExportIcon />
                    Exporter
                    {isExportingProductsXlsx && <CircularProgress size={20} />}
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    sx={{
                        marginRight: '16px',
                    }}
                    onClick={() => navigate(PRODUCTS_ROUTES.IMPORT_PRODUCTS)}
                    disabled={!isUserAdmin}
                >
                    <ImportExportIcon />
                    Importer
                </Button>
            </div>
            <Paper
                sx={{
                    marginTop: '16px',
                }}
            >
                <div
                    style={{ display: 'flex', alignItems: 'center', padding: '16px', gap: '16px' }}
                >
                    <TextField
                        variant="outlined"
                        label="Recherche"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                    />
                    <div style={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        sx={{ height: '36px' }}
                        disabled={productsDisabled}
                        onClick={navigateAndUpdateProduct}
                    >
                        {isUserAdmin ? 'Modifier' : 'Voir'}
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{ height: '36px' }}
                        disabled={productsDisabled}
                        onClick={navigateToProductDetails}
                    >
                        Voir
                    </Button>
                </div>
                <SimpleDataGrid
                    columns={productColDefs}
                    rows={productRows}
                    loading={products.isLoading || deleteProductMut.isPending}
                    onRowSelectionModelChange={(newSelection) =>
                        setproductsSelection(newSelection as number[])
                    }
                />
            </Paper>
        </Page>
    );
};

export default ProductsPage;
